import React, { useState, useEffect, useRef } from 'react';

const Tab = ({
  position = 'top',
  image,
  alt = 'Tab',
  onClick,
  isVisible = true,
  sliverSize = 10,
  className = '',
  children,
  storageKey = null, // Optional localStorage key for persisting position
  constrainToEdge = true, // Whether to constrain dragging to the edge
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [tabOffset, setTabOffset] = useState(() => {
    // Load saved position from localStorage if storageKey provided
    if (storageKey && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed === 'number' && parsed >= 0) {
            return parsed;
          }
        }
      } catch (e) {
        console.warn(`Failed to parse ${storageKey} from localStorage:`, e);
      }
    }
    // Default: will be centered in useEffect if constrainToEdge is true
    return null; // null means "not set yet, use default centered"
  });

  // Set initial position if not loaded from storage
  useEffect(() => {
    if (tabOffset === null && constrainToEdge && typeof window !== 'undefined') {
      const tabSize = 48;
      let initialOffset;
      if (position === 'top' || position === 'bottom') {
        // Header tab should appear closer to the right side (80% from left)
        if (storageKey === 'headerTabPosition') {
          initialOffset = Math.max(0, window.innerWidth * 0.8 - tabSize / 2);
        } else {
          // Other tabs: centered
          initialOffset = Math.max(0, (window.innerWidth - tabSize) / 2);
        }
      } else {
        initialOffset = Math.max(0, (window.innerHeight - tabSize) / 2);
      }
      setTabOffset(initialOffset);
    }
  }, [tabOffset, constrainToEdge, position, storageKey]);
  
  const dragStartRef = useRef({ x: 0, y: 0, startOffset: 0 });
  const tabRef = useRef(null);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined' && tabOffset !== null) {
      localStorage.setItem(storageKey, JSON.stringify(tabOffset));
    }
  }, [tabOffset, storageKey]);

  // Validate and clamp position when window resizes
  useEffect(() => {
    if (!constrainToEdge || tabOffset === null) return;
    
    const validatePosition = () => {
      if (typeof window === 'undefined') return;
      
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tabSize = 48;
      
      setTabOffset(currentOffset => {
        if (currentOffset === null) return currentOffset;
        
        let newOffset = currentOffset;
        
        if (position === 'top' || position === 'bottom') {
          // Clamp horizontal offset
          const maxOffset = Math.max(0, viewportWidth - tabSize);
          newOffset = Math.max(0, Math.min(currentOffset, maxOffset));
        } else {
          // Clamp vertical offset
          const maxOffset = Math.max(0, viewportHeight - tabSize);
          newOffset = Math.max(0, Math.min(currentOffset, maxOffset));
        }
        
        return newOffset !== currentOffset ? newOffset : currentOffset;
      });
    };
    
    validatePosition();
    window.addEventListener('resize', validatePosition);
    
    return () => {
      window.removeEventListener('resize', validatePosition);
    };
  }, [position, constrainToEdge, tabOffset]);

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left mouse button
    
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startOffset: tabOffset !== null ? tabOffset : 0,
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !constrainToEdge) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    const moveThreshold = 5; // Pixels before considered a drag

    // Only start dragging if moved beyond threshold
    if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tabSize = 48;

      if (position === 'top' || position === 'bottom') {
        // Horizontal movement only
        const newOffset = Math.max(0, Math.min(viewportWidth - tabSize, dragStartRef.current.startOffset + deltaX));
        setTabOffset(newOffset);
      } else if (position === 'left' || position === 'right') {
        // Vertical movement only
        const newOffset = Math.max(0, Math.min(viewportHeight - tabSize, dragStartRef.current.startOffset + deltaY));
        setTabOffset(newOffset);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      const moveThreshold = 5;

      // If didn't move much, treat as click
      if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
        onClick?.();
      }
    }
    setIsDragging(false);
  };

  // Add global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, tabOffset, position, constrainToEdge]);

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      startOffset: tabOffset !== null ? tabOffset : 0,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !constrainToEdge) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStartRef.current.x;
    const deltaY = touch.clientY - dragStartRef.current.y;
    const moveThreshold = 5;

    if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tabSize = 48;

      if (position === 'top' || position === 'bottom') {
        const newOffset = Math.max(0, Math.min(viewportWidth - tabSize, dragStartRef.current.startOffset + deltaX));
        setTabOffset(newOffset);
      } else if (position === 'left' || position === 'right') {
        const newOffset = Math.max(0, Math.min(viewportHeight - tabSize, dragStartRef.current.startOffset + deltaY));
        setTabOffset(newOffset);
      }
    }
    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (isDragging) {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - dragStartRef.current.x;
      const deltaY = touch.clientY - dragStartRef.current.y;
      const moveThreshold = 5;

      if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
        onClick?.();
      }
    }
    setIsDragging(false);
  };

  // Get border styles - remove border on edge that touches viewport
  const getBorderStyle = () => {
    const baseBorder = '1px solid rgba(13, 13, 13, 0.9)';
    
    switch (position) {
      case 'top':
        return {
          borderLeft: baseBorder,
          borderRight: baseBorder,
          borderBottom: baseBorder,
          borderTop: 'none', // No border on top edge
        };
      case 'bottom':
        return {
          borderLeft: baseBorder,
          borderRight: baseBorder,
          borderTop: baseBorder,
          borderBottom: 'none', // No border on bottom edge
        };
      case 'left':
        return {
          borderTop: baseBorder,
          borderRight: baseBorder,
          borderBottom: baseBorder,
          borderLeft: 'none', // No border on left edge
        };
      case 'right':
        return {
          borderTop: baseBorder,
          borderLeft: baseBorder,
          borderBottom: baseBorder,
          borderRight: 'none', // No border on right edge
        };
      default:
        return { border: baseBorder };
    }
  };

  // Get positioning styles based on position prop
  const getPositionStyle = () => {
    const baseStyle = {
      position: 'fixed',
      zIndex: 9999,
      cursor: isDragging ? 'grabbing' : 'grab',
      transition: isDragging ? 'none' : 'all 200ms ease-out',
      backgroundColor: '#FFFFFF',
      ...getBorderStyle(),
    };

    switch (position) {
      case 'top':
        return {
          ...baseStyle,
          top: 0,
          left: constrainToEdge && tabOffset !== null ? `${tabOffset}px` : '50%',
          transform: constrainToEdge && tabOffset !== null
            ? 'none' 
            : 'translateX(-50%)',
          width: '48px',
          height: isVisible ? '48px' : `${sliverSize}px`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        };
      case 'bottom':
        return {
          ...baseStyle,
          bottom: 0,
          left: constrainToEdge && tabOffset !== null ? `${tabOffset}px` : '50%',
          transform: constrainToEdge && tabOffset !== null
            ? 'none' 
            : 'translateX(-50%)',
          width: '48px',
          height: isVisible ? '48px' : `${sliverSize}px`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        };
      case 'left':
        return {
          ...baseStyle,
          left: 0,
          top: constrainToEdge && tabOffset !== null ? `${tabOffset}px` : '50%',
          transform: constrainToEdge && tabOffset !== null
            ? 'none' 
            : 'translateY(-50%)',
          width: isVisible ? '48px' : `${sliverSize}px`,
          height: '48px',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        };
      case 'right':
        return {
          ...baseStyle,
          right: 0,
          top: constrainToEdge && tabOffset !== null ? `${tabOffset}px` : '50%',
          transform: constrainToEdge && tabOffset !== null
            ? 'none' 
            : 'translateY(-50%)',
          width: isVisible ? '48px' : `${sliverSize}px`,
          height: '48px',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        };
      default:
        return baseStyle;
    }
  };

  // Get inner content style to handle sliver state
  const getInnerContentStyle = () => {
    const baseStyle = {
      width: '48px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    if (isVisible) {
      return baseStyle;
    }

    // When in sliver state, position content so only sliver is visible
    switch (position) {
      case 'top':
        return {
          ...baseStyle,
          transform: `translateY(${sliverSize - 48}px)`,
        };
      case 'bottom':
        return {
          ...baseStyle,
          transform: `translateY(${48 - sliverSize}px)`,
        };
      case 'left':
        return {
          ...baseStyle,
          transform: `translateX(${sliverSize - 48}px)`,
        };
      case 'right':
        return {
          ...baseStyle,
          transform: `translateX(${48 - sliverSize}px)`,
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div
      ref={tabRef}
      className={`tab-component ${className}`}
      data-header-toggle-tab={storageKey === 'headerTabPosition' ? 'true' : undefined}
      style={getPositionStyle()}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={getInnerContentStyle()}
          className="hover:opacity-60 transition-opacity"
        >
          {image && (
            <img
              src={image}
              alt={alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: storageKey === 'headerTabPosition' ? 'cover' : 'contain',
                padding: storageKey === 'headerTabPosition' ? '0' : '8px',
                pointerEvents: 'none', // Prevent image from interfering with drag
              }}
            />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default Tab;
